import React, { useState } from 'react';
import { Box, DialogActions, DialogContent, DialogTitle, Alert, TextField } from '@mui/material';
import useAuth from 'auth/UseAuth';
import { useForm } from 'react-hook-form';
import { CancelButton } from 'components/CancelButton';
import { PrimaryButton } from 'components/PrimaryButton';
import { AutocompleteWrapper } from 'components/AutocompleteWrapper';
import { DialogCloseButton } from 'components/DialogCloseButton';
import { DialogOpenButton } from 'components/DialogOpenButton';
import { ErrorIndicator } from 'components/ErrorIndicator';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { WarningIndicator } from 'components/WarningIndicator';
import { ErrorManagement, LoadingState } from '../../components/LoadingStateUtil';
import { SuperDuperFiestaEntity, IssueDispositionConfig, FlagIssues } from 'data/SuperDuperFiestaData';
import {
  PatientApprovalPatientMeasurement,
  PatientMeasurementIssueUpdate,
  UpdatePatientMeasurementIssues,
} from 'data/PatientApprovalData';
import { FormTextField } from 'components/form/FormTextField';
import useSdfIssues from 'components/hooks/UseSdfIssues';
import { PersistentDialog } from '../../components/PersistentDialog';

interface FormValues {
  issueId: number;
  description: string;
}

const defaultFormValues: FormValues = {
  issueId: 0,
  description: '',
};

export interface FlaggedRow {
  rowId: string;
  pipelineId: string;
}

interface FlagIssuesModalProps {
  selectedEntity: SuperDuperFiestaEntity | null;
  flaggedRows: FlaggedRow[];
  open?: boolean;
  onClose?: () => void;
  patientMeasurementUpdate?: {
    researchProjectId: string;
    deidentity: string;
    measurementData: PatientMeasurementIssueUpdate;
    onMeasurementsUpdated: (measurements: PatientApprovalPatientMeasurement[]) => void;
  };
}

export const FlagIssuesModal = ({
  selectedEntity,
  flaggedRows,
  open: externalOpen = false,
  onClose,
  patientMeasurementUpdate,
}: FlagIssuesModalProps) => {
  const { accessToken } = useAuth();
  const { issues } = useSdfIssues();
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = externalOpen || internalOpen;

  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });

  const issueId = form.watch('issueId');

  const { reset, control, watch, trigger } = form;

  const handleOpen = async () => {
    setInternalOpen(true);
  };

  const handleSave = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      if (!selectedEntity) {
        throw new Error('No entity selected');
      }

      const { description, issueId } = watch();

      if (patientMeasurementUpdate) {
        const { measurementData, onMeasurementsUpdated } = patientMeasurementUpdate;
        measurementData.issues = [...measurementData.issues, issueId];
        const measurements = await UpdatePatientMeasurementIssues(
          patientMeasurementUpdate.researchProjectId,
          patientMeasurementUpdate.deidentity,
          patientMeasurementUpdate.measurementData,
          accessToken
        );
        if (measurements) {
          onMeasurementsUpdated(measurements);
        }
      }

      const flagConfig: IssueDispositionConfig[] = flaggedRows.map(({ rowId, pipelineId }) => ({
        rowId,
        issueId,
        pipelineId,
        description: description.trim(),
      }));

      await FlagIssues({ entityId: selectedEntity.entityId }, flagConfig, accessToken);
      handleClose();
    });
  };

  const handleClose = () => {
    reset(defaultFormValues);
    setInternalOpen(false);
    setLoadingState({ status: 'NotStarted' });
    onClose?.();
  };

  return (
    <>
      <DialogOpenButton title='Flag' onClick={handleOpen} disabled={flaggedRows.length === 0} />
      <PersistentDialog open={isOpen} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>
          <DialogCloseButton onClick={handleClose} />
          Flag Issue
        </DialogTitle>
        <DialogContent>
          <Box component='form' sx={{ flexGrow: 1, mt: 1 }}>
            <Alert severity='info' sx={{ mb: 2 }}>
              Flag selected rows
            </Alert>
            <AutocompleteWrapper
              fullWidth
              sx={{ mb: 1 }}
              size='small'
              value={issues.find(issue => issue.issueId === issueId) || null}
              options={issues}
              getOptionLabel={issue => issue.name}
              renderInput={params => <TextField {...params} label={'Issue'} margin='dense' required />}
              onChange={(_, value) => form.setValue('issueId', value?.issueId || 0)}
            />
            <FormTextField
              name='description'
              control={control}
              label='Enter a description for the flagging row'
              trigger={trigger}
              additionalProps={{ multiline: true, rows: 6 }}
            />
          </Box>
        </DialogContent>
        <LoadingIndicator loadingState={loadingState} margin={'LR'} />
        <ErrorIndicator loadingState={loadingState} />
        <WarningIndicator loadingState={loadingState} />
        <DialogActions>
          <CancelButton onClick={handleClose} disabled={loadingState.status === 'Loading'}>
            Cancel
          </CancelButton>
          <PrimaryButton onClick={handleSave} disabled={loadingState.status === 'Loading'}>
            Save
          </PrimaryButton>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};
